import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { courseModules } from '../data/courseModules';
import Quiz from '../components/Quiz';
import FeedbackForm from '../components/FeedbackForm';
import ModuleMenu from '../components/ModuleMenu';
import VideoPlayer from '../components/VideoPlayer'; // Import VideoPlayer
import { useAudioPlayer } from '../context/AudioPlayerContext'; // Import useAudioPlayer
import SnakesAndLaddersGame from './SnakesAndLadders'; // Import the game component
import useMediaQuery from '../utils/useMediaQuery'; // Import useMediaQuery
import './ModuleDetailsPage.css'; // Import the new CSS file
import { useTranslation } from 'react-i18next';
import { getBlobUrl } from '../utils/getBlobUrl';

const checkUrl = async (url, type = 'pdf') => {
    try {
        const response = await fetch(url, { method: 'HEAD' });
        const contentType = response.headers.get('Content-Type');
        return response.ok && contentType && contentType.toLowerCase().includes(type);
    } catch (error) {
        return false;
    }
};

const BackgroundCarousel = ({ module }) => {
    const image = module?.hero?.bgImage || module?.cardImage;

    if (!image) {
        return null;
    }

    const images = [image];

    const settings = {
        dots: false,
        infinite: images.length > 1,
        speed: 500,
        fade: true,
        slidesToShow: 1,
        slidesToScroll: 1,
        autoplay: images.length > 1,
        autoplaySpeed: 3000,
        arrows: false,
    };

    return (
        <div className="background-carousel">
            <Slider {...settings}>
                {images.map((image, index) => (
                    <div key={index}>
                        <img className="carousel-image" src={image} alt={module.title} style={{ width: '100%', objectFit: 'contain', border: '2px solid white' }} />
                    </div>
                ))}
            </Slider>
        </div>
    );
};

const ModuleDetailsPage = () => {
    const { moduleId, pageNumber, contentType } = useParams();
    const navigate = useNavigate();
    const { i18n } = useTranslation();
    const module = courseModules.find(m => m.id === `module-${moduleId}`);
    const [currentPage, setCurrentPage] = useState(0);
    const [viewMode, setViewMode] = useState('landing'); // landing, content
    const lastScrollY = useRef(0);
    const isMobile = useMediaQuery('(max-width: 768px)');
    const carouselModules = ['preprologue','prologue','1','2','3', '4', '5','6', '7', '8', '9', '10','11', 'epilogue'];
    const { pauseTrack, isPlaying, togglePlayPause, currentTime, duration, seekTo } = useAudioPlayer(); // Use the global audio player

    const feedbackQuestions = {
        '1': [
            { id: 'q1', text: 'Are you facing any obstacle in your journey to SAI?', label: 'Are you facing any obstacle in your journey to SAI?', type: 'text', answer: '' },
            { id: 'q2', text: 'Any other feedback on Module 1?', label: 'Any other feedback on Module 1?', type: 'text', answer: '' }
        ],
        '2': [
            { id: 'q1', text: 'Do you wish to learn Veda chanting?', label: 'Do you wish to learn Veda chanting?', type: 'boolean', answer: false },
            { id: 'q2', text: 'Would you like to volunteer to teach Vedas to other aspirants?', label: 'Would you like to volunteer to teach Vedas to other aspirants?', type: 'boolean', answer: false },
            { id: 'q3', text: 'Any other feedback on Module 2?', label: 'Any other feedback on Module 2?', type: 'text', answer: '' }
        ],
        '3': [
            { id: 'q1', text: 'Please let us know how our Guru Bhagwan Sathya Sai Baba has impacted you in your life journey to God?', label: 'Please let us know how our Guru Bhagwan Sathya Sai Baba has impacted you in your life journey to God?', type: 'text', answer: '' },
            { id: 'q2', text: 'Any other feedback on Module 3?', label: 'Any other feedback on Module 3?', type: 'text', answer: '' }
        ],
        '4': [
            { id: 'q1', text: 'Which Vedic principle you think will help you in your life the most?', label: 'Which Vedic principle you think will help you in your life the most?', type: 'text', answer: '' },
            { id: 'q2', text: 'Any other feedback on Module 4?', label: 'Any other feedback on Module 4?', type: 'text', answer: '' }
        ],
        '5': [
            { id: 'q1', text: 'Please share your idea on how we can encourage modern youth to pursue the path of Brahmacarya?', label: 'Please share your idea on how we can encourage modern youth to pursue the path of Brahmacarya?', type: 'text', answer: '' },
            { id: 'q2', text: 'Any other feedback on Module 5?', label: 'Any other feedback on Module 5?', type: 'text', answer: '' }
        ],
        '6': [
            { id: 'q1', text: 'What do you think is the biggest challenge to Grihasthas today and share your thoughts on how Vedic living may help here to progress towards God?', label: 'What do you think is the biggest challenge to Grihasthas today and share your thoughts on how Vedic living may help here to progress towards God?', type: 'text', answer: '' },
            { id: 'q2', text: 'Any other feedback on Module 6?', label: 'Any other feedback on Module 6?', type: 'text', answer: '' }
        ],
        '7': [
            { id: 'q1', text: 'What is your experience in applying Karma Yoga to Professional setting?', label: 'What is your experience in applying Karma Yoga to Professional setting?', type: 'text', answer: '' },
            { id: 'q2', text: 'Any other feedback on Module 7?', label: 'Any other feedback on Module 7?', type: 'text', answer: '' }
        ],
        '8': [
            { id: 'q1', text: 'Swami says hands in society and heads in forest. Share your experience if any on putting this teaching into practise.', label: 'Swami says hands in society and heads in forest. Share your experience if any on putting this teaching into practise.', type: 'text', answer: '' },
            { id: 'q2', text: 'Any other feedback on Module 8?', label: 'Any other feedback on Module 8?', type: 'text', answer: '' }
        ]
    };

    // Dynamically construct the PDF source based on language
    // This logic is moved outside of JSX to avoid parser quirks with complex template literals in attributes.
    // It now includes a fallback mechanism to English if the language-specific PDF is not found.
    const currentLang = i18n.language || 'en';
    const pdfFileName = module?.pdfPath?.split('/').pop();
    const [displayPdfSource, setDisplayPdfSource] = useState('');
    const [pdfLoading, setPdfLoading] = useState(true);
    const [menuAvailability, setMenuAvailability] = useState({
        main: false,
        atglance: false,
        keytakeway: false,
        video: false
    });

    useEffect(() => {
        const checkAllPdfs = async () => {
            let mainLang = false, mainEn = false, atglance = false, keytakeway = false;

            if (pdfFileName) {
                const langPath = getBlobUrl(`/${currentLang}/Modules/${pdfFileName}`);
                const enPath = getBlobUrl(`/en/Modules/${pdfFileName}`);
                const atGlancePath = getBlobUrl(`/enAtGlance/${pdfFileName}`);
                const keyTakeawayPath = getBlobUrl(`/enKeytakeaway/${pdfFileName}`);

                [mainLang, mainEn, atglance, keytakeway] = await Promise.all([
                    currentLang !== 'en' ? checkUrl(langPath) : Promise.resolve(false),
                    checkUrl(enPath),
                    checkUrl(atGlancePath),
                    checkUrl(keyTakeawayPath)
                ]);
            }

            const videoPath = `https://kvtsaistorageprod.blob.core.windows.net/envideo/m${moduleId}.mp4`;
            const video = await checkUrl(videoPath, 'video');

            setMenuAvailability({
                main: mainLang || mainEn,
                atglance,
                keytakeway,
                video
            });
        };

        checkAllPdfs();
    }, [pdfFileName, currentLang, moduleId]);

    useEffect(() => {
        if (!pdfFileName) {
            setDisplayPdfSource('');
            setPdfLoading(false);
            return;
        }

        const checkPdfExists = async () => {
            setPdfLoading(true);
            
            let langPath = getBlobUrl(`/${currentLang}/Modules/${pdfFileName}`);
            let enPath = getBlobUrl(`/en/Modules/${pdfFileName}`);

            if (contentType === 'atglance') {
                // Common file for all languages
                langPath = getBlobUrl(`/enAtGlance/${pdfFileName}`);
                enPath = getBlobUrl(`/enAtGlance/${pdfFileName}`);
            } else if (contentType === 'keytakeway') {
                // Common file for all languages
                langPath = getBlobUrl(`/enKeytakeaway/${pdfFileName}`);
                enPath = getBlobUrl(`/enKeytakeaway/${pdfFileName}`);
            }

            // 1. Try the current language path (if it's not English)
            if (currentLang !== 'en' && await checkUrl(langPath)) {
                setDisplayPdfSource(langPath + '#toolbar=0');
            // 2. Otherwise, try the English path as a fallback
            } else if (await checkUrl(enPath)) {
                setDisplayPdfSource(enPath + '#toolbar=0');
            // 3. If neither is found, set path to empty
            } else {
                setDisplayPdfSource('');
            }

            setPdfLoading(false);
        };

        checkPdfExists();
    }, [currentLang, pdfFileName, contentType]); // Re-run when language or pdfFileName changes

    useEffect(() => {
        if (!isMobile) {
            document.body.classList.remove('header-hidden');
            return;
        }

        const handleScroll = () => {
            const currentScrollY = window.scrollY;
            if (currentScrollY > lastScrollY.current && currentScrollY > 100) {
                document.body.classList.add('header-hidden');
            } else {
                document.body.classList.remove('header-hidden');
            }
            lastScrollY.current = currentScrollY <= 0 ? 0 : currentScrollY;
        };

        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            window.removeEventListener('scroll', handleScroll);
            document.body.classList.remove('header-hidden');
        };
    }, [isMobile]);

    useEffect(() => {
        if (contentType) {
            setViewMode('pdf');
            return;
        }
        if (pageNumber) {
            if (pageNumber === 'quiz') {
                setViewMode('quiz');
                return;
            }
            if (pageNumber === 'feedback') {
                setViewMode('feedback');
                return;
            }
            if (pageNumber === 'video') {
                setViewMode('video');
                return;
            }
            if (pageNumber === 'game') {
                setViewMode('game');
                return;
            }
            if (pageNumber === 'contents') {
                setViewMode('contents_menu');
                return;
            }
            const pageIndex = parseInt(pageNumber, 10) - 1;
            if (!isNaN(pageIndex) && pageIndex >= 0 && module && pageIndex < module.pages.length) {
                setCurrentPage(pageIndex);
                setViewMode('content');
            } else if (pageNumber === 'pdf') {
                setViewMode('pdf');
            }
            else {
                // Invalid page number, redirect to landing
                navigate(`/module/${moduleId}`);
            }
        } else {
            // No page number, set to landing
            setViewMode('landing');
        }
    }, [moduleId, pageNumber, module, navigate, contentType]);

    useEffect(() => {
        // Don't autoplay audio. Pause if navigating to a view that shouldn't have audio.
        if (viewMode === 'video' || viewMode === 'pdf') {
            pauseTrack();
        }
    }, [viewMode, pauseTrack]);

    useEffect(() => {
        // When the component unmounts (e.g., navigating to a new module), pause the audio.
        // This ensures audio from a previous module doesn't carry over.
        return () => pauseTrack();
    }, [pauseTrack]);

    // Custom play button handler for Module 10
    const handlePlayButtonClick = () => {
        if (moduleId === '10') {
            navigate(`/module/${moduleId}/game`);
        } else {
            togglePlayPause();
        }
    };

    if (viewMode === 'game') {
        if (moduleId === '10') {
            return (
                <div className="game-container">
                    <SnakesAndLaddersGame />
                </div>
            );
        }
        return <div>Game not available for this module.</div>;
    }

    if (!module) {
        return <div>Module not found</div>;
    }

    const page = module.pages[currentPage];

    if (viewMode === 'content' && !page) {
        return <div>Page not found</div>;
    }

    const navigateToPage = (pageIndex) => {
        if (pageIndex >= 0 && pageIndex < module.pages.length) {
            navigate(`/module/${moduleId}/${pageIndex + 1}`);
        }
    }

    const handleContents = () => {
        if (module && module.pdfPath) {
            navigate(`/module/${moduleId}/contents`);
        } else {
            navigateToPage(0);
        }
    }

    const handleVideo = () => {
        navigate(`/module/${moduleId}/video`);
    }

    const handleFeedback = () => {
        navigate(`/module/${moduleId}/feedback`);
    }

    const handleBackToBanner = () => {
        navigate(`/module/${moduleId}`);
    }

    const handleBackToContents = () => {
        navigate(`/module/${moduleId}/contents`);
    }

    const renderPageContent = () => {
        if (!page) return null;

        switch (page.pageId) {
            case 'definition':
                return (
                    <div className="module-content-section">
                        <h2>{page.definition.title}</h2>
                        <p>{page.definition.text}</p>
                        {page.definition.quote && (
                            <blockquote className="module-content-blockquote">
                                <p><em>{page.definition.quote.sanskrit}</em></p>
                                <p>{page.definition.quote.meaning}</p>
                                <footer>- {page.definition.quote.origin}</footer>
                            </blockquote>
                        )}
                    </div>
                );
            case 'origin':
                return (
                    <div className="module-content-section">
                        <h2>{page.origin.title}</h2>
                        {page.origin.cards.map((card, index) => (
                            <div key={index} className="module-relevance-item">
                                <h3>{card.title}</h3>
                                <p>{card.text}</p>
                                <p><em>Source: {card.source}</em></p>
                            </div>
                        ))}
                    </div>
                );
            case 'shruti':
                return (
                    <div className="module-content-section">
                        <h2>{page.shruti.title}</h2>
                        <p>{page.shruti.text}</p>
                        <p><strong>{page.shruti.highlight}</strong></p>
                        <hr />
                        <h2>{page.limitless.title}</h2>
                        <blockquote>
                            <p>{page.limitless.quote}</p>
                            <footer>- {page.limitless.source}</footer>
                        </blockquote>
                    </div>
                );
            case 'structure':
                return (
                    <div className="module-content-section">
                        <h2>{page.structure.title}</h2>
                        <p>{page.structure.description}</p>
                        <div className="module-grid-container">
                            {page.structure.cards.map(card => (
                                <div key={card.id} className="module-card">
                                    <h3>{card.title}</h3>
                                    <p>{card.desc}</p>
                                    <p><em>{card.source}</em></p>
                                </div>
                            ))}
                        </div>
                    </div>
                );
            case 'purpose':
                return (
                    <div className="module-content-section">
                        <h2>{page.purpose.essence.title}</h2>
                        <p>{page.purpose.essence.text}</p>
                        <p>{page.purpose.essence.subText}</p>
                        <p><em>Source: {page.purpose.essence.source}</em></p>
                        <hr />
                        <h2>{page.purpose.universality.title}</h2>
                        <p>{page.purpose.universality.text}</p>
                        <p>{page.purpose.universality.subText}</p>
                        <p><em>Source: {page.purpose.universality.source}</em></p>
                    </div>
                );
            case 'sound':
                return (
                    <div className="module-content-section">
                        <h2>{page.sound.title}</h2>
                        <p>{page.sound.description}</p>
                        <p>{page.sound.subDescription}</p>
                    </div>
                );
            case 'preservation':
                return (
                    <div className="module-content-section">
                        <h2>{page.preservation.title}</h2>
                        <p>{page.preservation.text}</p>
                        <h3>{page.preservation.subTitle}</h3>
                        <ul>
                            {page.preservation.techniques.map(t => <li key={t}>{t}</li>)}
                        </ul>
                        <p>{page.preservation.footer}</p>
                    </div>
                );
            case 'relevance':
                return (
                    <div className="module-content-section">
                        <h2>{page.relevance.title}</h2>
                        {page.relevance.content.map((item, index) => (
                            <div key={index} className="module-relevance-item">
                                <h3>{item.q}</h3>
                                <p>{item.a}</p>
                            </div>
                        ))}
                    </div>
                );
            case 'quiz':
                return <Quiz questions={page.quiz.questions} />;
            case 'feedback':
                return (
                    <div className="feedback-container">
                        <FeedbackForm key={moduleId} moduleId={module.id} questions={feedbackQuestions[moduleId] || []} />
                    </div>
                );
            default:
                return (
                    <div>
                        <button onClick={() => navigateToPage(currentPage - 1)} disabled={currentPage === 0}>
                            Previous
                        </button>
                        <span>Page {currentPage + 1} of {module.pages.length}</span>
                        <button onClick={() => navigateToPage(currentPage + 1)} disabled={currentPage === module.pages.length - 1}>
                            Next
                        </button>
                    </div>
                );
        }
    };

    return (
        <div className="module-page-wrapper">
            {carouselModules.includes(moduleId) && viewMode === 'landing' && <BackgroundCarousel module={module} />}
            {viewMode === 'landing' ? (
                <div 
                className="module-landing-page" 
                style={{ backgroundImage: !carouselModules.includes(moduleId) ? `url(${module.hero.bgImage})` : 'none' }}
                >
                    <div className="landing-overlay">
                        <ModuleMenu
                            module={module}
                            moduleId={moduleId}
                            navigateToPage={navigateToPage}
                            handleContents={handleContents}
                            handleVideo={handleVideo}
                            handleFeedback={handleFeedback}
                            isPlaying={isPlaying && moduleId !== '10'} // Audio is not "playing" if we're going to the game
                            togglePlay={handlePlayButtonClick}
                            currentTime={currentTime}
                            duration={duration}
                            onSeek={seekTo}
                            onImage={true}
                            isMobile={isMobile}
                            hasVideo={menuAvailability.video}
                        />
                    </div>
                </div>
            ) : viewMode === 'quiz' ? (
                <div className="module-details-page quiz-page">
                    <Quiz questions={module.pages.find(p => p.pageId === 'quiz')?.quiz.questions || []} />
                    <ModuleMenu
                        module={module}
                        moduleId={moduleId}
                        navigateToPage={navigateToPage}
                        handleContents={handleContents}
                        handleVideo={handleVideo}
                        handleFeedback={handleFeedback}
                        isPlaying={isPlaying && moduleId !== '10'}
                        togglePlay={togglePlayPause}
                        currentTime={currentTime}
                        duration={duration}
                        onSeek={seekTo}
                        onImage={false}
                        handleBack={handleBackToBanner}
                        isMobile={isMobile}
                        hasVideo={menuAvailability.video}
                    />
                </div>
            ) : viewMode === 'feedback' ? (
                <div className="module-details-page feedback-page">
                    <div className="feedback-content-wrapper" style={{ width: '100%', maxWidth: '600px', margin: '0 auto', padding: '20px', background: 'rgba(255,255,255,0.95)', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
                        <h2 style={{ textAlign: 'center', color: '#0b3a7a', marginBottom: '20px', textTransform: 'capitalize' }}>Module {moduleId} Feedback</h2>
                        
                        <FeedbackForm key={moduleId} moduleId={module.id} questions={feedbackQuestions[moduleId]} />
                    </div>
                    <ModuleMenu
                        module={module}
                        moduleId={moduleId}
                        navigateToPage={navigateToPage}
                        handleContents={handleContents}
                        handleVideo={handleVideo}
                        handleFeedback={handleFeedback}
                        isPlaying={isPlaying && moduleId !== '10'}
                        togglePlay={togglePlayPause}
                        currentTime={currentTime}
                        duration={duration}
                        onSeek={seekTo}
                        onImage={false}
                        handleBack={handleBackToBanner}
                        isMobile={isMobile}
                        hasVideo={menuAvailability.video}
                    />
                </div>
            ) : viewMode === 'contents_menu' ? (
                <div className="module-details-page contents-menu-page" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <div className="content-card" style={{ padding: '2rem', display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%', maxWidth: '400px' }}>
                        <h2 style={{ textAlign: 'center', color: '#0b3a7a' }}>Module Contents</h2>
                        {menuAvailability.main && <button className="btn-primary" onClick={() => navigate(`/module/${moduleId}/contents/main/pdf`)}>Contents</button>}
                        {menuAvailability.atglance && <button className="btn-primary" onClick={() => navigate(`/module/${moduleId}/contents/atglance/pdf`)}>At a Glance</button>}
                        {menuAvailability.keytakeway && <button className="btn-primary" onClick={() => navigate(`/module/${moduleId}/contents/keytakeway/pdf`)}>Key Takeaways</button>}
                    </div>
                    <ModuleMenu
                        module={module}
                        moduleId={moduleId}
                        navigateToPage={navigateToPage}
                        handleContents={handleContents}
                        handleVideo={handleVideo}
                        handleFeedback={handleFeedback}
                        isPlaying={isPlaying && moduleId !== '10'}
                        togglePlay={togglePlayPause}
                        currentTime={currentTime}
                        duration={duration}
                        onSeek={seekTo}
                        onImage={false}
                        handleBack={handleBackToBanner}
                        isMobile={isMobile}
                        hasVideo={menuAvailability.video}
                    />
                </div>
            ) : viewMode === 'pdf' ? (
                <div className="module-details-page pdf-page" onContextMenu={(e) => e.preventDefault()}>
                   {module && module.pdfPath ? (
                        pdfLoading ? (
                            <div>Loading PDF...</div>
                        ) : displayPdfSource ? (
                            isMobile ? (
                                <embed
                                    src={displayPdfSource}
                                    type="application/pdf"
                                    style={{ flex: 1, width: '100%' }}
                                />
                            ) : (
                                <object
                                    data={displayPdfSource}
                                    type="application/pdf"
                                    style={{ flex: 1, width: '100%' }}
                                >
                                    <p>Your browser does not support embedded PDFs. Please use a compatible browser to view the content.</p>
                                </object>
                            )
                        ) : (
                            <div style={{ color: 'white', textAlign: 'center', marginTop: '2rem' }}>File doesn't Exist</div>
                        )
                    ) : (
                        <div style={{ color: 'white', textAlign: 'center', marginTop: '2rem' }}>File doesn't Exist</div>
                    )}
                    <ModuleMenu
                        module={module}
                        moduleId={moduleId}
                        navigateToPage={navigateToPage}
                        handleContents={handleContents}
                        handleVideo={handleVideo}
                        handleFeedback={handleFeedback}
                        isPlaying={isPlaying && moduleId !== '10'}
                        togglePlay={togglePlayPause}
                        currentTime={currentTime}
                        duration={duration}
                        onSeek={seekTo}
                        onImage={false}
                        handleBack={contentType ? handleBackToContents : handleBackToBanner}
                        isMobile={isMobile}
                        hasVideo={menuAvailability.video}
                    />
                </div>
            ) : viewMode === 'video' ? (
                <div className="module-details-page video-page" style={{  minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
                        <video
                            controls
                            autoPlay
                            controlsList="nodownload"
                            style={{ width: '100%', maxWidth: '1000px', maxHeight: '80vh', borderRadius: '8px' }}
                            src={`https://kvtsaistorageprod.blob.core.windows.net/envideo/m${moduleId}.mp4`}
                        >
                            Your browser does not support the video tag.
                        </video>
                    </div>
                    <ModuleMenu
                        module={module}
                        moduleId={moduleId}
                        navigateToPage={navigateToPage}
                        handleContents={handleContents}
                        handleVideo={handleVideo}
                        handleFeedback={handleFeedback}
                        isPlaying={isPlaying && moduleId !== '10'}
                        togglePlay={togglePlayPause}
                        currentTime={currentTime}
                        duration={duration}
                        onSeek={seekTo}
                        onImage={false}
                        handleBack={handleBackToBanner}
                        isMobile={isMobile}
                        hasVideo={menuAvailability.video}
                    />
                </div>
            )
            
         
             : (
                <div className="module-details-page">
                        <React.Fragment>
                            {module.hero && (
                                <div className="module-hero" style={{ backgroundImage: `url(${module.hero.bgImage})` }}>
                                </div>
                            )}

                            <div className="module-page-header">
                                <h1>{module.title}</h1>
                                <p>{module.subtitle}</p>
                                <h2 className="module-page-title">{page.title}</h2>
                            </div>

                            {module.video && module.video.id && (
                                <VideoPlayer videoId={module.video.id} />
                            )}

                            {renderPageContent()}
                        </React.Fragment>

                    <ModuleMenu
                        module={module}
                        moduleId={moduleId}
                        navigateToPage={navigateToPage}
                        handleContents={handleContents}
                        handleVideo={handleVideo}
                        handleFeedback={handleFeedback}
                        isPlaying={isPlaying && moduleId !== '10'}
                        togglePlay={togglePlayPause}
                        currentTime={currentTime}
                        duration={duration}
                        onSeek={seekTo}
                        onImage={false}
                        handleBack={handleBackToBanner}
                        isMobile={isMobile}
                        hasVideo={menuAvailability.video}
                    />
                </div>
            )}
        </div>
    );
};

export default ModuleDetailsPage;